// components/ServerDatabase/ServerCard.jsx
import React from 'react';

const ServerCard = ({
    ip,
    banner,
    icon,
    status,
    players,
    version,
    tags,
    protection,
    lastTested,
    successRate,
    numericIp,
    isPopular,
    onHover,
    onHoverEnd,
    onStartTesting
  }) => {
    const statusConfig = {
      online: { 
        dot: 'status-online', 
        label: 'Online', 
        color: 'text-accent-green',
        dotColor: 'bg-accent-green',
        pulse: 'animate-ping-slow' // Added pulse animation
      },
      offline: { 
        dot: 'status-offline', 
        label: 'Offline', 
        color: 'text-accent-red',
        dotColor: 'bg-accent-red'
      },
      restarting: {
        dot: 'status-restarting',
        label: 'Restarting',
        color: 'text-accent-orange',
        dotColor: 'bg-accent-orange',
        pulse: 'animate-pulse-slow'
      }
    };
  
    const protectionConfig = {
      protected: { 
        label: 'Protected', 
        color: 'text-accent-pink',
        bg: 'bg-accent-pink/20',
        icon: 'fa-shield-alt'
      },
      vulnerable: { 
        label: 'Vulnerable', 
        color: 'text-accent-green',
        bg: 'bg-accent-green/20',
        icon: 'fa-bug'
      },
      medium: { 
        label: 'Medium Risk', 
        color: 'text-accent-yellow',
        bg: 'bg-accent-yellow/20',
        icon: 'fa-exclamation-triangle'
      }
    };
  
    const currentStatus = statusConfig[status] || statusConfig.online;
    const currentProtection = protectionConfig[protection] || protectionConfig.protected;
    const successColor = successRate > 60 ? 'text-accent-green' : successRate > 30 ? 'text-accent-yellow' : 'text-accent-red';
  
    return (
        <div
        className="bg-dark-600 rounded-2xl overflow-hidden server-card gradient-border cursor-pointer transform-gpu hover:scale-[1.03] transition-medium border border-dark-400 hover:border-accent-purple/30 hover:shadow-md group relative"
        onMouseEnter={onHover}
        onMouseLeave={onHoverEnd}
        // Optional: Make the whole card clickable to start testing if desired
        // onClick={onStartTesting}
      >
        {/* ... (banner, icon, content remain the same) ... */}
        <div className="relative h-32 bg-gradient-to-r from-dark-500 to-dark-400 overflow-hidden">
          <img 
            src={icon} 
            alt={`${ip} server icon banner`} // Более осмысленный alt
            className="w-full h-full object-cover opacity-30 group-hover:opacity-40 group-hover:scale-105 transition-medium" 
          />
          <div className="absolute left-5 -bottom-6 transform group-hover:-translate-y-1 transition-medium">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-accent-blue to-accent-purple p-0.5 group-hover:rotate-6 transition-medium">
              <div className="w-full h-full bg-dark-600 rounded-lg flex items-center justify-center overflow-hidden">
                <img src={icon} alt={`${ip} server icon`} className="w-full h-full object-cover group-hover:scale-110 transition-medium" />
              </div>
            </div>
          </div>
          {isPopular && ( <div className="absolute top-3 right-3 bg-accent-orange/20 text-accent-orange px-2 py-1 rounded-lg text-xs font-medium flex items-center transform-gpu group-hover:scale-110 transition-medium"> <i className="fas fa-fire mr-1 animate-pulse-slow"></i> Popular </div> )}
        </div>

        <div className="pt-10 pb-5 px-5 relative">
          <div className="flex justify-between items-start mb-3">
            <div>
              <h3 className="font-bold text-lg group-hover:text-accent-blue transition-medium">{ip}</h3>
              {/* ... status, players, version */}
              <div className="flex items-center text-xs text-gray-400 mt-1 flex-wrap gap-2 group-hover:text-gray-300 transition-medium">
                <span className="flex items-center"> <span className={`w-2 h-2 rounded-full ${currentStatus.dotColor} mr-1 ${currentStatus.pulse || ''}`}></span> <span className={currentStatus.color}>{currentStatus.label}</span> </span>
                <span className="mx-2 hidden sm:inline">•</span> <span className="flex items-center"> <i className="fas fa-users mr-1 text-xs"></i> {players?.toLocaleString()} </span>
                <span className="mx-2 hidden sm:inline">•</span> <span className="flex items-center"> <i className="fas fa-code-branch mr-1 text-xs"></i> {version} </span>
              </div>
            </div>
             <span className={`${currentProtection.bg} ${currentProtection.color} px-2 py-1 rounded-lg text-xs font-medium flex items-center transform-gpu group-hover:scale-105 transition-medium`}> <i className={`fas ${currentProtection.icon} mr-1`}></i> {currentProtection.label} </span>
          </div>
          <p className="text-sm text-gray-400 mb-4 line-clamp-2 group-hover:text-gray-300 transition-medium"> Server description placeholder text goes here... </p>
          <div className="flex gap-2 mb-4 flex-wrap">
            {tags?.map((tag, index) => ( <span key={index} className="bg-dark-500 text-xs px-2 py-1 rounded-lg group-hover:bg-dark-400 transition-medium transform-gpu hover:scale-105"> {tag} </span> ))}
          </div>
          <div className="space-y-2 text-xs text-gray-400 mb-4 group-hover:text-gray-300 transition-medium">
            <div className="flex justify-between items-center"> <span className="flex items-center"> <i className="fas fa-network-wired mr-1"></i> IP: </span> <span className="text-white font-mono hover:text-accent-blue transition-medium cursor-pointer" title="Click to copy"> {numericIp} </span> </div>
            <div className="flex justify-between items-center"> <span className="flex items-center"> <i className="fas fa-clock mr-1"></i> Last Tested: </span> <span>{lastTested}</span> </div>
            <div className="flex justify-between items-center"> <span className="flex items-center"> <i className="fas fa-chart-line mr-1"></i> Success Rate: </span> <div className="flex items-center"> <span className={`${successColor} mr-2`}>{successRate}%</span> <div className="w-16 h-1 rounded-full bg-dark-500 overflow-hidden"> <div className={`h-full ${successColor.replace('text-', 'bg-')} transition-all duration-500`} style={{ width: `${successRate}%` }} ></div> </div> </div> </div>
          </div>

          {/* Update button onClick */}
          <button
            onClick={onStartTesting} // Call the passed function
            className={`w-full py-3 text-sm font-medium rounded-lg flex items-center justify-center transition transform-gpu ${
              status === 'online'
                ? 'bg-gradient-to-r from-accent-purple to-accent-pink text-white hover:shadow-glow-pink hover:-translate-y-0.5 active:translate-y-0'
                : 'bg-dark-500 text-gray-400 cursor-not-allowed'
            }`}
            disabled={status !== 'online'}
          >
            <i className="fas fa-terminal mr-2"></i>
            Connect
            {status === 'online' && (
              <i className="fas fa-chevron-right ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-medium"></i>
            )}
          </button>
        </div>

        <div className="absolute -bottom-4 -right-4 w-16 h-16 opacity-10 bg-white rounded-full blur-xl group-hover:opacity-20 transition-medium"></div>
      </div>
    );
  };

export default ServerCard;